import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router';
import {
  Search, Plus, Building2,
  Users, MapPin, X, LayoutGrid, List, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import { ClinicStatus } from '../types/clinic';
import ClinicCreationWizard from '../components/wizard/ClinicCreationWizard';
import SimpleAddClinicModal from '../components/SimpleAddClinicModal';
import ResizablePanel from '@shared/components/common/ResizablePanel';
import ClinicDetailPanel from '../components/ClinicDetailPanel';
import RequirePermission from '@shared/components/RequirePermission';
import { usePermissions, ClinicMgmtPermissions } from '@shared/context/usePermissions';
import './ClinicListPage.css';

type StatusFilter = 'Active' | 'Inactive' | '';
type ViewMode = 'card' | 'grid';
type SortField = 'name' | 'code' | 'status' | 'city' | 'state';
type SortDir = 'asc' | 'desc';

/** Map URL ?status= numeric value to the local StatusFilter string */
function statusParamToFilter(param: string | null): StatusFilter {
  if (param === '1') return 'Active';
  if (param === '2') return 'Inactive';
  return '';
}

/** Map local StatusFilter string to the API numeric status (or undefined for "all") */
function statusFilterToApiValue(filter: StatusFilter): number | undefined {
  if (filter === 'Active') return 1;
  if (filter === 'Inactive') return 2;
  return undefined;
}

export default function ClinicListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive initial status filter from URL param (e.g. /clinics?status=1)
  const initialStatus = statusParamToFilter(searchParams.get('status'));

  // Card view state
  const [cardSearch, setCardSearch] = useState('');
  const [cardStatusFilter, setCardStatusFilter] = useState<StatusFilter>(initialStatus);

  // Grid view state
  const [gridSearch, setGridSearch] = useState('');
  const [gridStatusFilter, setGridStatusFilter] = useState<StatusFilter>(initialStatus);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isSimpleAddOpen, setIsSimpleAddOpen] = useState(false);
  const [page, setPage] = useState(1);

  // The "Add a Clinic" button branches on clinic-editing permission:
  // editors get the full wizard; everyone else gets the simple add (which also
  // opens an urgent Clinic Network setup task). Both require create permission,
  // enforced on the button itself, so the two flows are mutually exclusive.
  const { hasPermission } = usePermissions();
  const canUseFullWizard = hasPermission(ClinicMgmtPermissions.EditClinicProfile);

  // Initialize view mode from URL param (e.g. /clinics?view=grid when navigating back)
  const [viewMode, setViewMode] = useState<ViewMode>(
    searchParams.get('view') === 'grid' ? 'grid' : 'card'
  );
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Get selected clinic from URL or state
  const selectedClinicIdFromUrl = searchParams.get('selected');
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(
    selectedClinicIdFromUrl ? Number.parseInt(selectedClinicIdFromUrl, 10) : null
  );
  const [activeTab, setActiveTab] = useState<string>('info');

  // Use the active view's search/status for the API query
  const activeSearch = viewMode === 'card' ? cardSearch : gridSearch;
  const activeStatusFilter = viewMode === 'card' ? cardStatusFilter : gridStatusFilter;
  const apiStatus = statusFilterToApiValue(activeStatusFilter);

  const pageSize = 25;

  // Reset to the first page whenever the result set changes (search/status/view).
  // Done during render (React's recommended alternative to an effect) so the page
  // index never lags the filters into an out-of-range query.
  const [pagedFilters, setPagedFilters] = useState({ search: activeSearch, status: apiStatus, view: viewMode });
  if (
    pagedFilters.search !== activeSearch ||
    pagedFilters.status !== apiStatus ||
    pagedFilters.view !== viewMode
  ) {
    setPagedFilters({ search: activeSearch, status: apiStatus, view: viewMode });
    setPage(1);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['clinics', activeSearch, apiStatus, page],
    queryFn: () => clinicApi.getClinics(page, pageSize, activeSearch || undefined, apiStatus),
    // Keep the previous page on screen while the next one loads so the list and
    // pager don't flash empty on every page change.
    placeholderData: keepPreviousData,
  });

  // Trust the backend's paging metadata rather than recomputing — it stays in
  // sync with the server's clamping and hasNext/hasPrevious flags.
  const totalPages = data?.totalPages ?? 1;

  const formatCount = (n: number) => n.toLocaleString();

  // Close status dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  // Filter clinics by status and capabilities — card view
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const cardFilteredClinics = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((clinic) => {
      // Status filter — keyed off the effective status the API derives
      // (SLV2-3195), so the filter always agrees with the row badge.
      if (cardStatusFilter === 'Active' && clinic.status === ClinicStatus.Inactive) return false;
      if (cardStatusFilter === 'Inactive' && clinic.status !== ClinicStatus.Inactive) return false;

      return true;
    });
  }, [data?.items, cardStatusFilter]);

  // Filter clinics by status and capabilities — grid view
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const gridFilteredClinics = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((clinic) => {
      // Status filter — same effective-status source as the row badge (SLV2-3195).
      if (gridStatusFilter === 'Active' && clinic.status === ClinicStatus.Inactive) return false;
      if (gridStatusFilter === 'Inactive' && clinic.status !== ClinicStatus.Inactive) return false;

      return true;
    });
  }, [data?.items, gridStatusFilter]);

  // Sort clinics for grid view
  const sortedClinics = useMemo(() => {
    const sorted = [...gridFilteredClinics];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'code':
          cmp = (a.code ?? '').localeCompare(b.code ?? '');
          break;
        case 'status':
          cmp = a.status - b.status;
          break;
        case 'city':
          cmp = (a.city ?? '').localeCompare(b.city ?? '');
          break;
        case 'state':
          cmp = (a.state ?? '').localeCompare(b.state ?? '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [gridFilteredClinics, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Handle clinic card click - select for detail pane
  const handleClinicCardClick = (clinicId: number) => {
    setSelectedClinicId(clinicId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('selected', String(clinicId));
    setSearchParams(newParams, { replace: true });
  };

  // Handle close detail panel
  const handleCloseDetailPanel = () => {
    setSelectedClinicId(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('selected');
    setSearchParams(newParams, { replace: true });
  };

  // Handle quick action button click
  const handleCardAction = (clinicId: number, tab: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedClinicId(clinicId);
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('selected', String(clinicId));
    setSearchParams(newParams, { replace: true });
  };

  const handleGridRowClick = (clinicId: number) => {
    navigate(`/clinics/${clinicId}`);
  };

  const handleCardStatusChange = (value: StatusFilter) => {
    setCardStatusFilter(value);
    const newParams = new URLSearchParams(searchParams);
    const apiVal = statusFilterToApiValue(value);
    if (apiVal !== undefined) {
      newParams.set('status', String(apiVal));
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleCardClearFilters = () => {
    setCardSearch('');
    setCardStatusFilter('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('status');
    setSearchParams(newParams, { replace: true });
  };

  const handleOpenWizard = () => {
    if (canUseFullWizard) {
      setIsWizardOpen(true);
    } else {
      setIsSimpleAddOpen(true);
    }
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
  };

  const handleCloseSimpleAdd = () => {
    setIsSimpleAddOpen(false);
  };

  const handleWizardComplete = (clinicId: number) => {
    setIsWizardOpen(false);
    setIsSimpleAddOpen(false);
    queryClient.invalidateQueries({ queryKey: ['clinics'] });
    setSelectedClinicId(clinicId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('selected', String(clinicId));
    setSearchParams(newParams, { replace: true });
  };

  const cardHasActiveFilters =
    cardSearch !== '' ||
    cardStatusFilter !== '';

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const handleStatusFilterSelect = (value: StatusFilter) => {
    setGridStatusFilter(value);
    setStatusDropdownOpen(false);
    const newParams = new URLSearchParams(searchParams);
    const apiVal = statusFilterToApiValue(value);
    if (apiVal !== undefined) {
      newParams.set('status', String(apiVal));
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams, { replace: true });
  };

  // Shared pager — rendered under both the card list and the grid table.
  const paginationBar = !isLoading && data && data.totalPages > 1 ? (
    <div className="clinic-pagination">
      <button
        type="button"
        className="clinic-page-btn"
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={!data.hasPreviousPage}
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
        Prev
      </button>
      <span className="clinic-page-status">
        Page {data.pageNumber} of {formatCount(data.totalPages)}
      </span>
      <button
        type="button"
        className="clinic-page-btn"
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={!data.hasNextPage}
        aria-label="Next page"
      >
        Next
        <ChevronRight size={16} />
      </button>
    </div>
  ) : null;

  // ─── Grid view ───
  const gridContent = (
    <div className="clinic-grid-wrapper">
      {/* Grid toolbar */}
      <div className="clinic-grid-toolbar">
        <div className="clinic-grid-search">
          <Search size={16} className="clinic-grid-search-icon" />
          <input
            type="text"
            placeholder="Search clinics..."
            value={gridSearch}
            onChange={(e) => setGridSearch(e.target.value)}
            className="clinic-grid-search-input"
            aria-label="Search clinics"
          />
          {gridSearch && (
            <button
              type="button"
              className="clinic-grid-search-clear"
              onClick={() => setGridSearch('')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="clinic-grid-toolbar-right">
          {!isLoading && data && (
            <span className="clinic-grid-count">
              {gridFilteredClinics.length === data.totalCount
                ? `${formatCount(data.totalCount)} clinics`
                : `${formatCount(gridFilteredClinics.length)} of ${formatCount(data.totalCount)} clinics`}
            </span>
          )}
          <RequirePermission permission="UserCanCreateClinicProfile">
            <button
              className="add-clinic-btn"
              onClick={handleOpenWizard}
              title="Add a Clinic"
              aria-label="Add a Clinic"
              type="button"
            >
              <Plus size={18} />
            </button>
          </RequirePermission>
        </div>
      </div>

      {isLoading ? (
        <div className="clinic-list-loading">
          <div className="loading-spinner"></div>
          <p>Loading clinics...</p>
        </div>
      ) : error ? (
        <div className="clinic-list-empty">
          <p>Error loading clinics</p>
        </div>
      ) : sortedClinics.length === 0 ? (
        <div className="clinic-list-empty">
          <p>
            {gridSearch
              ? `No clinics found matching "${gridSearch}"`
              : 'No clinics found'}
          </p>
        </div>
      ) : (
        <div className="clinic-table-wrapper">
          <table className="clinic-table">
            <thead>
              <tr>
                <th className="col-sortable" onClick={() => handleSort('name')}>
                  Name {renderSortIcon('name')}
                </th>
                <th className="col-sortable" onClick={() => handleSort('code')}>
                  Code {renderSortIcon('code')}
                </th>
                <th className="col-sortable col-status-header" ref={statusDropdownRef as React.RefObject<HTMLTableCellElement>}>
                  <span
                    className="col-status-trigger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusDropdownOpen((prev) => !prev);
                    }}
                  >
                    Status
                    {gridStatusFilter && (
                      <span className="col-status-active-dot" />
                    )}
                    <Filter size={10} className="col-status-filter-icon" />
                  </span>
                  {statusDropdownOpen && (
                    <div className="col-status-dropdown">
                      <button
                        type="button"
                        className={`col-status-option ${gridStatusFilter === '' ? 'active' : ''}`}
                        onClick={() => handleStatusFilterSelect('')}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className={`col-status-option ${gridStatusFilter === 'Active' ? 'active' : ''}`}
                        onClick={() => handleStatusFilterSelect('Active')}
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        className={`col-status-option ${gridStatusFilter === 'Inactive' ? 'active' : ''}`}
                        onClick={() => handleStatusFilterSelect('Inactive')}
                      >
                        Inactive
                      </button>
                    </div>
                  )}
                </th>
                <th className="col-sortable" onClick={() => handleSort('city')}>
                  City {renderSortIcon('city')}
                </th>
                <th className="col-sortable" onClick={() => handleSort('state')}>
                  State {renderSortIcon('state')}
                </th>
                <th>Phone</th>
                <th>Capabilities</th>
                <th>Last Verified</th>
              </tr>
            </thead>
            <tbody>
              {sortedClinics.map((clinic) => (
                <tr
                  key={clinic.id}
                  className="clinic-table-row"
                  onClick={() => handleGridRowClick(clinic.id)}
                >
                  <td className="col-name">{clinic.name}</td>
                  <td className="col-code">{clinic.code ?? ''}</td>
                  <td>
                    <span className={`status-badge ${clinic.statusCssClass}`}>
                      {clinic.statusDisplay}
                    </span>
                  </td>
                  <td>{clinic.city ?? ''}</td>
                  <td>{clinic.state ?? ''}</td>
                  <td>{clinic.phone ?? ''}</td>
                  <td className="col-capabilities">
                    <div className="capability-badges">
                      {clinic.hasPAT && <span className="capability-badge" title="Physical Ability Testing">PAT</span>}
                      {clinic.hasXRay && <span className="capability-badge" title="X-Ray">X-Ray</span>}
                      {clinic.fitTestType && <span className="capability-badge" title={`Fit Testing: ${clinic.fitTestType}`}>Fit</span>}
                      {!clinic.hasPAT && !clinic.hasXRay && !clinic.fitTestType && <span className="capability-none">—</span>}
                    </div>
                  </td>
                  <td title={clinic.lastVerifiedByUserName ? `Verified by ${clinic.lastVerifiedByUserName}` : ''}>
                    {clinic.lastVerifiedDate
                      ? new Date(clinic.lastVerifiedDate).toLocaleDateString()
                      : 'Not verified'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paginationBar}
    </div>
  );

  // ─── Card view left pane (header + list) ───
  const cardListPane = (
    <div className="clinic-list-pane">
      {/* Card view header with filters — inside the left pane */}
      <div className="clinic-list-header">
        <div className="clinic-list-header-top">
          <div className="page-title-row">
            <Building2 size={24} />
            <h1>Clinic Network</h1>
          </div>
          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              title="Card View"
              aria-label="Card View"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
              aria-label="Grid View"
            >
              <List size={16} />
            </button>
          </div>
        </div>

        <div className="clinic-search-row">
          <div className="clinic-search">
            <Search size={16} className="search-icon-left" />
            <input
              type="text"
              placeholder="Search clinics..."
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              className="search-input"
              aria-label="Search clinics"
            />
            {cardSearch && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setCardSearch('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <RequirePermission permission="UserCanCreateClinicProfile">
            <button
              className="add-clinic-btn"
              onClick={handleOpenWizard}
              title="Add a Clinic"
              aria-label="Add a Clinic"
              type="button"
            >
              <Plus size={18} />
            </button>
          </RequirePermission>
        </div>

        <div className="clinic-filter-row">
          <div className="status-pills" role="group" aria-label="Filter by status">
            <button
              type="button"
              className={`status-pill ${cardStatusFilter === '' ? 'active' : ''}`}
              onClick={() => handleCardStatusChange('')}
            >
              All
            </button>
            <button
              type="button"
              className={`status-pill status-pill-active ${cardStatusFilter === 'Active' ? 'active' : ''}`}
              onClick={() => handleCardStatusChange('Active')}
            >
              Active
            </button>
            <button
              type="button"
              className={`status-pill status-pill-inactive ${cardStatusFilter === 'Inactive' ? 'active' : ''}`}
              onClick={() => handleCardStatusChange('Inactive')}
            >
              Inactive
            </button>
          </div>

          {cardHasActiveFilters && (
            <button
              className="clear-filters-btn"
              onClick={handleCardClearFilters}
              title="Clear all filters"
              type="button"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        {!isLoading && data && (
          <div className="clinic-results-count">
            {cardFilteredClinics.length === data.totalCount
              ? `${formatCount(data.totalCount)} clinics`
              : `${formatCount(cardFilteredClinics.length)} of ${formatCount(data.totalCount)} clinics`}
          </div>
        )}
      </div>

      <div className="clinic-list-content">
        {isLoading ? (
          <div className="clinic-list-loading">
            <div className="loading-spinner"></div>
            <p>Loading clinics...</p>
          </div>
        ) : error ? (
          <div className="clinic-list-empty">
            <p>Error loading clinics</p>
          </div>
        ) : cardFilteredClinics.length === 0 ? (
          <div className="clinic-list-empty">
            <p>
              {cardSearch
                ? `No clinics found matching "${cardSearch}"`
                : 'No clinics found'}
            </p>
          </div>
        ) : (
          <div className="clinic-cards">
            {cardFilteredClinics.map((clinic) => (
              <div
                key={clinic.id}
                className={`clinic-card ${selectedClinicId === clinic.id ? 'selected' : ''}`}
                onClick={() => handleClinicCardClick(clinic.id)}
              >
                <div className="clinic-card-header">
                  <div className="clinic-card-top-row">
                    <div className="clinic-card-icon">
                      <Building2 size={20} />
                    </div>
                    <span className={`status-badge ${clinic.statusCssClass}`}>
                      {clinic.statusDisplay}
                    </span>
                  </div>

                  <h3 className="clinic-name">{clinic.name}</h3>
                  <div className="clinic-meta">
                    <span>ID: {clinic.id}</span>
                    {clinic.code && <span> &middot; Code: {clinic.code}</span>}
                  </div>
                </div>

                <div className="clinic-card-actions">
                  <span className="clinic-card-dash">&mdash;</span>
                  <div className="clinic-card-action-group">
                    <button
                      type="button"
                      className="clinic-action-btn"
                      onClick={(e) => handleCardAction(clinic.id, 'info', e)}
                      title="Clinic Info"
                      aria-label="View clinic info"
                    >
                      <Building2 size={16} />
                    </button>

                    <button
                      type="button"
                      className="clinic-action-btn"
                      onClick={(e) => handleCardAction(clinic.id, 'contacts', e)}
                      title="Contacts"
                      aria-label="View clinic contacts"
                    >
                      <Users size={16} />
                    </button>

                    <button
                      type="button"
                      className="clinic-action-btn"
                      onClick={(e) => handleCardAction(clinic.id, 'addresses', e)}
                      title="Addresses"
                      aria-label="View clinic addresses"
                    >
                      <MapPin size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {paginationBar}
    </div>
  );

  // Right panel: Detail view or empty state (card mode only)
  const detailPane = (
    <div className="clinic-detail-pane">
      {selectedClinicId ? (
        <ClinicDetailPanel
          clinicId={selectedClinicId}
          onClose={handleCloseDetailPanel}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      ) : (
        <div className="empty-state">
          <div className="empty-state-content">
            <Building2 size={48} strokeWidth={1.5} />
            <h3>Select a clinic</h3>
            <p>Click a card to view details</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`clinic-list-page ${viewMode === 'grid' ? 'clinic-list-page-grid' : ''}`}>
      {/* Grid view: page-level header with title + toggle, then self-contained grid */}
      {viewMode === 'grid' && (
        <div className="clinic-list-header clinic-list-header-slim">
          <div className="clinic-list-header-top">
            <div className="page-title-row">
            <Building2 size={24} />
            <h1>Clinic Network</h1>
          </div>
            <div className="view-toggle">
              <button
                type="button"
                className="view-toggle-btn"
                onClick={() => setViewMode('card')}
                title="Card View"
                aria-label="Card View"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                type="button"
                className="view-toggle-btn active"
                onClick={() => setViewMode('grid')}
                title="Grid View"
                aria-label="Grid View"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View content */}
      {viewMode === 'grid' ? (
        gridContent
      ) : (
        <div className="clinic-list-body">
          <ResizablePanel
            leftPanel={cardListPane}
            rightPanel={detailPane}
            defaultLeftWidth={400}
            minLeftWidth={300}
            maxLeftWidth={600}
            storageKey="clinic-list-panel-width"
          />
        </div>
      )}

      {/* Clinic Creation Wizard Modal (editors / admins) */}
      <ClinicCreationWizard
        isOpen={isWizardOpen}
        onClose={handleCloseWizard}
        onComplete={handleWizardComplete}
      />

      {/* Simple Add Clinic Modal (non-editors) — mounted only while open */}
      {isSimpleAddOpen && (
        <SimpleAddClinicModal
          onClose={handleCloseSimpleAdd}
          onComplete={handleWizardComplete}
        />
      )}
    </div>
  );
}
